import React, { useState, useEffect } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import Recaptcha from "react-recaptcha";
import {
  Alert,
  Button,
  Checkbox,
  Comment,
  Divider,
  Input,
  notification,
} from "antd";
import { SendOutlined } from "@ant-design/icons";
import { navigate } from "gatsby";

const { TextArea } = Input;

const encode = (data) => {
  return Object.keys(data)
    .map((key) => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
    .join("&");
};

function ContactForm({ message }) {
  const [token, setToken] = useState(null);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://www.google.com/recaptcha/api.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  }, []);

  return (
    <Formik
      initialValues={{ fullName: "", email: "", message: "" }}
      initialErrors={{
        fullName: "Enter your name",
      }}
      validate={(values) => {
        const errors = {};
        if (!values.acceptTerms) {
          errors.acceptTerms = "This is required";
        }
        if (!values.fullName) {
          errors.fullName = "Your name is required";
        } else if (values.fullName.length <= 1) {
          errors.fullName = "Your name must be at least 2 characters";
        }
        if (!values.message) {
          errors.message = "A message is required";
        }
        if (!values.email) {
          errors.email = "An email address is required";
        } else if (
          !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
        ) {
          errors.email = "This is not a valid email address";
        }
        return errors;
      }}
      onSubmit={(data) => {
        if (token !== null) {
          fetch("/", {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: encode({
              "form-name": "contact-form",
              ...data,
              "g-recaptcha-response": token,
            }),
          })
            .then(() => {
              notification.open({
                message: "Thank you!",
                duration: 5,
                type: "success",
                description: "We will get back to you shortly.",
              });
              navigate("/");
            })
            .catch((error) => {
              notification.open({
                message: "Ah oh!",
                duration: 5,
                type: "error",
                description: { error },
              });
            });
        }
      }}
    >
      {(props) => (
        <Form
          name="contact-form"
          data-netlify="true"
          data-netlify-honeypot="bot-field"
          data-netlify-recaptcha="true"
        >
          <Field type="hidden" name="form-name" />
          <Field type="hidden" name="bot-field" />
          <Comment
            author="Robbert van Zuiden"
            avatar="https://avatars2.githubusercontent.com/u/3952837?s=460&u=a800b577d2c9d4ffebb11ec65b4c99ef758423fd&v=4"
            content={message}
          />
          <label
            htmlFor="fullName"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            Name: *
          </label>
          <ErrorMessage
            name="fullName"
            render={(msg) => (
              <Alert
                message={msg}
                style={{ fontSize: "12px" }}
                type="error"
                showIcon
              />
            )}
          />
          <Field
            name="fullName"
            as={Input}
            placeholder="Enter your name"
            type="text"
            style={{ marginBottom: "16px" }}
          />

          <label
            htmlFor="email"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            Email address: *
          </label>
          <ErrorMessage
            name="email"
            render={(msg) => (
              <Alert
                message={msg}
                style={{ fontSize: "12px" }}
                type="error"
                showIcon
              />
            )}
          />
          <Field
            name="email"
            as={Input}
            placeholder="Enter your email address"
            type="text"
            style={{ marginBottom: "16px" }}
          />

          <label
            htmlFor="organisation"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            Organisation:
          </label>
          <ErrorMessage
            name="organisation"
            render={(msg) => (
              <Alert
                message={msg}
                style={{ fontSize: "12px" }}
                type="error"
                showIcon
              />
            )}
          />
          <Field
            name="organisation"
            as={Input}
            placeholder="Enter your organisation"
            type="text"
            rows={4}
            style={{ marginBottom: "16px" }}
          />

          <label
            htmlFor="message"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            Message: *
          </label>
          <ErrorMessage
            style={{ marginBottom: "16px" }}
            name="message"
            render={(msg) => (
              <Alert
                message={msg}
                style={{ fontSize: "12px" }}
                type="error"
                showIcon
              />
            )}
          />
          <Field
            name="message"
            as={TextArea}
            placeholder="Enter your message"
            rows={4}
          />
          <Divider dashed />
          <label
            htmlFor="acceptTerms"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            <Field
              name="acceptTerms"
              as={Checkbox}
              type="checkbox"
              style={{ marginRight: "8px" }}
            />{" "}
            I understand that the information provided by me is subject to the{" "}
            <a
              href="https://www.iubenda.com/privacy-policy/16163444"
              target="_blank"
              rel="noreferrer noopener"
            >
              Privacy Policy
            </a>
          </label>
          <ErrorMessage
            name="acceptTerms"
            render={(msg) => (
              <Alert
                message={msg}
                style={{ fontSize: "12px" }}
                type="error"
                showIcon
              />
            )}
          />
          <label
            htmlFor="acceptNewsletter"
            style={{
              display: "block",
              fontSize: "14px",
              marginTop: "16px",
              marginBottom: "8px",
            }}
          >
            <Field
              name="acceptNewsletter"
              as={Checkbox}
              type="checkbox"
              style={{ marginRight: "8px" }}
            />{" "}
            In addition to receiving email about my project, I agree to
            inflowchart.com sending me a newsletter with updates about its
            product and services. I understand that the newsletter is sent once
            or twice every 3 months and my information will never be sold to any
            third parties.
          </label>
          {props.isValid && (
            <Recaptcha
              sitekey={process.env.SITE_RECAPTCHA_KEY}
              render="explicit"
              verifyCallback={(response) => {
                setToken(response);
              }}
              onloadCallback={() => {
                console.log("done loading!");
              }}
            />
          )}
          <Button
            type="primary"
            size="large"
            disabled={!props.isValid}
            loading={props.isSubmitting}
            htmlType="submit"
            style={{ marginTop: "16px" }}
            icon={<SendOutlined />}
          >
            Send message
          </Button>
        </Form>
      )}
    </Formik>
  );
}

export default ContactForm;
